import { useMutation } from "@tanstack/react-query";
import { useNavigate } from "react-router";
import { toast } from "sonner";

import { useDialogState } from "@/hooks/use-dialog-state";
import client from "@/lib/client";
import { LGT_SERVER_URL } from "@/lib/env";
import useStore from "@/state";

import {
  createCustomVerificationType,
  createOrganization,
  createSKU,
  deleteCustomVerificationType,
  deleteImage,
  deleteOrganization,
  deleteSKU,
  deleteTag,
  deleteTrack,
  deleteVideo,
  deployContract,
  exportOwners,
  exportSKUs,
  exportTags,
  exportTagVerifications,
  forceMetadataUpdate,
  forceMint,
  getAuditLogs as requestAuditLogs,
  resequenceSKU,
  transferSKU,
  transferTags,
  updateImage,
  updateSKU,
  updateVideo,
} from "./api";
import { RequestService } from "./requests";
import {
  CustomVerificationType,
  DeployContractArgs,
  Image,
  SKU,
  TransferTagsBody,
  Video,
} from "./types";

export const useCreateSKUMutation = () => {
  const navigate = useNavigate();

  return useMutation({
    mutationKey: ["skus", "create"],
    mutationFn: async (params: Partial<SKU>) => {
      return createSKU(params);
    },
    onSuccess: (data) => {
      client.invalidateQueries({ queryKey: ["skus"] });
      toast.success("Created SKU", {
        description: `SKU ${data.id} created successfully.`,
      });

      navigate(`/skus/${data.id}`);
    },
    onError: (event) => {
      toast.error("Failed to create SKU", {
        description: event.message,
      });
    },
  });
};

export const useUpdateSKUMutation = (skuId: string) => {
  const { closeDialog } = useDialogState();

  return useMutation({
    mutationKey: ["skus", "update", skuId],
    mutationFn: async (params: Partial<SKU>) => {
      return updateSKU(skuId, params);
    },
    onSuccess: (data) => {
      client.invalidateQueries({ queryKey: ["skus", String(data.id)] });
      toast.success("Changes saved", {
        description: `SKU ${data.id} updated successfully.`,
      });
      closeDialog();
    },
    onError: (event) => {
      toast.error("Failed to update SKU", {
        description: event.message,
      });
    },
  });
};

export const useDeleteSKUMutation = () => {
  const navigate = useNavigate();
  const { dialog, closeDialog } = useDialogState();

  return useMutation({
    mutationKey: ["skus", "delete"],
    mutationFn: async (skuId: string) => {
      return deleteSKU(skuId);
    },
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ["skus"] });
      toast.success("Deleted SKU", {
        description: "SKU has been successfully deleted.",
      });
      navigate("/skus");
    },
    onSettled: () => {
      if (dialog?.name === "delete-sku") {
        closeDialog();
      }
    },
    onError: (event) => {
      toast.error("Failed to delete SKU", {
        description: event.message,
      });
    },
  });
};

export const useTransferSKUMutation = () => {
  const navigate = useNavigate();
  const { closeDialog } = useDialogState();

  return useMutation({
    mutationKey: ["skus", "transfer"],
    mutationFn: async ({
      skuId,
      workOSOrgId,
    }: {
      skuId: string;
      workOSOrgId: string;
    }) => {
      return transferSKU(skuId, workOSOrgId);
    },
    onSuccess: () => {
      toast.success("Transferred SKU", {
        description: "SKU has been successfully transferred.",
      });
      client.invalidateQueries({ queryKey: ["skus"] });
      navigate("/skus");
    },
    onSettled: () => {
      closeDialog();
    },
    onError: (event) => {
      toast.error("Failed to transfer SKU", {
        description: event.message,
      });
    },
  });
};

export const useResequenceSKUMutation = () => {
  const { closeDialog } = useDialogState();

  return useMutation({
    mutationKey: ["skus", "resequence"],
    mutationFn: async ({ skuId }: { skuId: string }) => {
      return resequenceSKU(skuId);
    },
    onSuccess: () => {
      toast.success("Resequenced SKU", {
        description: "Tags in SKU have been successfully resequenced.",
      });
      client.invalidateQueries({ queryKey: ["skus"] });
      client.invalidateQueries({ queryKey: ["tags"] });
    },
    onSettled: () => {
      closeDialog();
    },
    onError: (event) => {
      toast.error("Failed to resequence tags in SKU", {
        description: event.message,
      });
    },
  });
};

export const useDeleteTagMutation = () => {
  const { closeDialog } = useDialogState();

  return useMutation({
    mutationKey: ["tags", "delete"],
    mutationFn: async (tagId: string) => {
      await deleteTag(tagId);

      return { tagId };
    },
    onSuccess: (data) => {
      client.invalidateQueries({ queryKey: ["tags"] });
      toast.success("Deleted Tag", {
        description: `Tag ${data.tagId} has been successfully deleted`,
      });
    },
    onSettled: () => {
      closeDialog();
    },
    onError: (event) => {
      toast.error("Failed to delete tag", {
        description: event.message,
      });
    },
  });
};

export const useTransferTagsMutation = (templateId: string) => {
  const { closeDialog } = useDialogState();

  return useMutation({
    mutationKey: ["tags", "transfer"],
    mutationFn: async (body: TransferTagsBody) => {
      return transferTags(body);
    },
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ["skus", templateId] });
      toast.success("Moved Tags", {
        description: "Tags have been successfully moved",
      });
    },
    onError: () => {
      toast.error("Failed to move tags", {
        description: "An error occurred while moving tags",
      });
    },
    onSettled: () => {
      closeDialog();
    },
  });
};

export const useCreateOrganizationMutation = () => {
  const { closeDialog } = useDialogState();

  return useMutation({
    mutationKey: ["organizations", "create"],
    mutationFn: (name: string) => {
      return createOrganization(name);
    },
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ["organizations"] });
      client.invalidateQueries({ queryKey: ["organization-memberships"] });
      toast.success("Created organization", {
        description: "New organization successfully created",
      });
    },
    onSettled: () => {
      closeDialog();
    },
    onError: (event) => {
      toast.error("Failed to create organization", {
        description: event.message,
      });
    },
  });
};

export const useDeleteOrganizationMutation = () => {
  const { closeDialog } = useDialogState();

  return useMutation({
    mutationKey: ["organizations", "delete"],
    mutationFn: (workOSOrganizationId: string) => {
      return deleteOrganization(workOSOrganizationId);
    },
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ["organizations"] });
      toast.success("Deleted Organization", {
        description: "Organization has been successfully deleted",
      });
    },
    onSettled: () => {
      closeDialog();
    },
    onError: (event) => {
      toast.error("Failed to delete organization", {
        description: event.message,
      });
    },
  });
};

export const useExportSKUsMutation = () => {
  return useMutation({
    mutationKey: ["skus", "export"],
    mutationFn: async () => {
      const response = await exportSKUs();
      const href = window.URL.createObjectURL(response);

      const a = document.createElement("a");
      a.href = href;

      a.download = `skus_${new Date().toISOString()}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
    },
    onSuccess: () => {
      toast.success("Downloaded CSV", {
        description: `SKUs have been downloaded`,
      });
    },
    onError: (event) => {
      toast.error("Failed to export SKU", {
        description: event.message,
      });
    },
  });
};

export const useExportTagsMutation = () => {
  return useMutation({
    mutationKey: ["tags", "export"],
    mutationFn: async () => {
      const response = await exportTags();
      const href = window.URL.createObjectURL(response);

      const a = document.createElement("a");
      a.href = href;

      a.download = `tags_${new Date().toISOString()}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
    },
    onSuccess: () => {
      toast.success("Downloaded CSV", {
        description: `Tags have been downloaded`,
      });
    },
    onError: (event) => {
      toast.error("Failed to export tags", {
        description: event.message,
      });
    },
  });
};

export const useExportTagVerificationsMutation = () => {
  return useMutation({
    mutationKey: ["verifications", "export"],
    mutationFn: async () => {
      const response = await exportTagVerifications();
      const href = window.URL.createObjectURL(response);

      const a = document.createElement("a");
      a.href = href;

      a.download = `verifications_${new Date().toISOString()}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
    },
    onSuccess: () => {
      toast.success("Downloaded CSV", {
        description: `Tag verifications have been downloaded`,
      });
    },
    onError: (event) => {
      toast.error("Failed to export tag verifications", {
        description: event.message,
      });
    },
  });
};

export const useExportOwnersMutation = () => {
  return useMutation({
    mutationKey: ["owners", "export"],
    mutationFn: async (templateId: string) => {
      const response = await exportOwners(templateId);
      const href = window.URL.createObjectURL(response);

      const a = document.createElement("a");
      a.href = href;

      a.download = `sku_${templateId}_owners_by_tag_id_${new Date().toISOString()}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();

      return { templateId };
    },
    onSuccess: (data) => {
      toast.success("Downloaded CSV", {
        description: `Owners have been downloaded for SKU ${data.templateId}`,
      });
    },
    onError: (event) => {
      toast.error("Failed to export owners", {
        description: event.message,
      });
    },
  });
};

export const useDeployContractMutation = () => {
  const { closeDialog } = useDialogState();

  return useMutation({
    mutationKey: ["skus", "deploy-contract"],
    mutationFn: async (args: DeployContractArgs) => {
      return deployContract(args);
    },
    onSuccess: (data) => {
      client.invalidateQueries({ queryKey: ["skus", String(data.id)] });
      toast.success("Contract deployed", {
        description: "Contract has been successfully deployed",
      });
    },
    onError: () => {
      toast.error("Failed to deploy contract", {
        description: "Failed to deploy contract",
      });
    },
    onSettled: () => {
      closeDialog();
    },
  });
};

export const useForceMetadataUpdateMutation = () => {
  return useMutation({
    mutationKey: ["skus", "force-metadata-update"],
    mutationFn: async (skuId: string) => {
      return forceMetadataUpdate(skuId);
    },
    onSuccess: () => {
      // TODO: Improve server response to narrow SKU down.
      client.invalidateQueries({ queryKey: ["skus"] });
      toast.success("Forced metadata update", {
        description: "Metadata has been successfully updated",
      });
    },
    onError: (event) => {
      toast.error("Failed to force metadata update", {
        description: event.message,
      });
    },
  });
};

export const useForceMintMutation = () => {
  return useMutation({
    mutationKey: ["skus", "force-mint"],
    mutationFn: async (skuId: string) => {
      return forceMint(skuId);
    },
    onSuccess: () => {
      // TODO: Improve server response to narrow SKU down.
      client.invalidateQueries({ queryKey: ["skus"] });
      toast.success("Forced mint", {
        description: "NFTs will be minted shortly.",
      });
    },
    onError: (event) => {
      toast.error("Failed to force mint", {
        description: event.message,
      });
    },
  });
};

export const useRequestAuditLogsMutation = () => {
  return useMutation({
    mutationKey: ["audit-logs", "request"],
    mutationFn: () => {
      return requestAuditLogs();
    },
    onSuccess: () => {
      toast.success("Requested audit logs", {
        description: "Audit logs will be available shortly.",
      });
    },
    onError: (event) => {
      toast.error("Failed to request audit logs", {
        description: event.message,
      });
    },
  });
};

export const useRefreshTokenMutation = () => {
  const navigate = useNavigate();
  const { setSession } = useStore();

  return useMutation({
    mutationKey: ["refresh-token"],
    mutationFn: async (organizationId: string) => {
      const requests = new RequestService();

      const token = await requests.refreshToken(organizationId);

      // FIXME: This portion is copied from the useAuthCallbackQuery hook.
      // We should standardize it to make sure the behaviors do not diverge.
      const sessionResponse = await fetch(`${LGT_SERVER_URL}/auth/session`, {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
      });

      if (!sessionResponse.ok) {
        throw new Error(
          `Failed to fetch session: ${sessionResponse.status} ${sessionResponse.statusText}`,
        );
      }

      const session = await sessionResponse.json();

      setSession(session);

      return token;
    },
    onSuccess: () => {
      navigate("/skus");
      client.invalidateQueries({ queryKey: ["skus"] });
    },
    onError: (event) => {
      toast.error("Failed to authenticate existing login session", {
        description: event.message,
      });
    },
  });
};

export const useCreateCustomVerificationTypeMutation = () => {
  const { closeDialog } = useDialogState();

  return useMutation({
    mutationKey: ["custom-verification-types", "create"],
    mutationFn: (verificationType: CustomVerificationType) => {
      return createCustomVerificationType(verificationType);
    },
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ["custom-verification-types"] });
      toast.success("Created Verification Type", {
        description: "New verification type successfully created",
      });
    },
    onSettled: () => {
      closeDialog();
    },
    onError: (event) => {
      toast.error("Failed to create custom verification type", {
        description: event.message,
      });
    },
  });
};

export const useDeleteCustomVerificationTypeMutation = () => {
  const { closeDialog } = useDialogState();

  return useMutation({
    mutationKey: ["custom-verification-types", "delete"],
    mutationFn: (verificationTypeId: string) => {
      return deleteCustomVerificationType(verificationTypeId);
    },
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ["custom-verification-types"] });
      toast.success("Deleted Verification Type", {
        description: "Verification type has been successfully deleted",
      });
    },
    onError: (event) => {
      toast.error("Failed to delete custom verification type", {
        description: event.message,
      });
    },
    onSettled: () => {
      closeDialog();
    },
  });
};

export const useEditVideoMutation = () => {
  const { dialog, closeDialog } = useDialogState();

  return useMutation({
    mutationKey: ["videos", "update"],
    mutationFn: async (video: Partial<Video>) => {
      return updateVideo(video);
    },
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ["videos"] });
      toast.success("Video Updated", {
        description: "Video has been successfully updated.",
      });
    },
    onSettled: () => {
      if (dialog?.name === "update-video") {
        closeDialog();
      }
    },
    onError: (event) => {
      toast.error("Failed to update video", {
        description: event.message,
      });
    },
  });
};

export const useDeleteVideoMutation = () => {
  const { dialog, closeDialog } = useDialogState();

  return useMutation({
    mutationKey: ["videos", "delete"],
    mutationFn: async (id: number) => {
      return deleteVideo(id);
    },
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ["videos"] });
      toast.success("Video Deleted", {
        description: "Video has been successfully deleted.",
      });
    },
    onSettled: () => {
      if (dialog?.name === "delete-video") {
        closeDialog();
      }
    },
    onError: (event) => {
      toast.error("Failed to delete video", {
        description: event.message,
      });
    },
  });
};

export const useEditImageMutation = () => {
  const { dialog, closeDialog } = useDialogState();
  return useMutation({
    mutationKey: ["images", "update"],
    mutationFn: async (image: Partial<Image>) => {
      return updateImage(image);
    },
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ["images"] });
      toast.success("Image Updated", {
        description: "Image has been successfully updated.",
      });
    },
    onSettled: () => {
      if (dialog?.name === "update-image") {
        closeDialog();
      }
    },
    onError: (event) => {
      toast.error("Failed to update image", {
        description: event.message,
      });
    },
  });
};

export const useDeleteImageMutation = () => {
  const { dialog, closeDialog } = useDialogState();

  return useMutation({
    mutationKey: ["images", "delete"],
    mutationFn: async (id: number) => {
      return deleteImage(id);
    },
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ["images"] });
      toast.success("Image Deleted", {
        description: "Image has been successfully deleted.",
      });
    },
    onSettled: () => {
      if (dialog?.name === "delete-image") {
        closeDialog();
      }
    },
    onError: (event) => {
      toast.error("Failed to delete image", {
        description: event.message,
      });
    },
  });
};

export const useEditTrackMutation = () => {
  const { dialog, closeDialog } = useDialogState();

  return useMutation({
    mutationKey: ["tracks", "update"],
    mutationFn: async (track: Partial<Video>) => {
      return updateVideo(track);
    },
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ["tracks"] });
      toast.success("Track Updated", {
        description: "Track has been successfully updated.",
      });
    },
    onSettled: () => {
      if (dialog?.name === "update-track") {
        closeDialog();
      }
    },
    onError: (event) => {
      toast.error("Failed to delete track", {
        description: event.message,
      });
    },
  });
};

export const useDeleteTrackMutation = () => {
  const { dialog, closeDialog } = useDialogState();

  return useMutation({
    mutationKey: ["tracks", "delete"],
    mutationFn: async (id: number) => {
      return deleteTrack(id);
    },
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ["tracks"] });
      toast.success("Track Deleted", {
        description: "Track has been successfully deleted.",
      });
    },
    onSettled: () => {
      if (dialog?.name === "delete-track") {
        closeDialog();
      }
    },
    onError: (event) => {
      toast.error("Failed to delete track", {
        description: event.message,
      });
    },
  });
};
